import React, { useState, useCallback, useRef, useEffect } from "react";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import Box from "@amzn/awsui-components-react/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Button from "@amzn/awsui-components-react/polaris/button";
import Input from "@amzn/awsui-components-react/polaris/input";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import type { CloudscapeJsonSchema } from "@amzn/fin-tech-cloudscape-json-forms";
import {
  saveSchema,
  listSchemas,
} from "@amzn/fin-tech-cloudscape-json-forms-studio";
import { SCHEMA_NAME_PATTERN, STUDIO_API_CONFIG } from "./constants";

export interface SaveSchemaModalProps {
  visible: boolean;
  onDismiss: () => void;
  schemaRef: React.MutableRefObject<CloudscapeJsonSchema>;
}

interface AlertState {
  type: "success" | "error";
  content: string;
}

const SaveSchemaModal: React.FC<SaveSchemaModalProps> = ({
  visible,
  onDismiss,
  schemaRef,
}) => {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const handleNameChange = useCallback((value: string) => {
    setName(value);
    if (!value) {
      setNameError("Name is required");
    } else if (!SCHEMA_NAME_PATTERN.test(value)) {
      setNameError(
        "Name must be alphanumeric, hyphens, or underscores (max 128 characters)"
      );
    } else {
      setNameError(undefined);
    }
  }, []);

  const checkForDuplicates = useCallback(
    async (schemaName: string): Promise<boolean> => {
      const listUrl = STUDIO_API_CONFIG.schemaApi.listUrl;
      const [draftRes, pendingRes] = await Promise.all([
        listSchemas(listUrl, "DRAFT"),
        listSchemas(listUrl, "PENDING_APPROVAL"),
      ]);

      if (!draftRes.success || !pendingRes.success) {
        throw new Error("Failed to check for existing schemas.");
      }

      const allItems = [...draftRes.items, ...pendingRes.items];
      return allItems.some(
        (item) => item.name.toLowerCase() === schemaName.toLowerCase()
      );
    },
    []
  );

  const handleSave = useCallback(
    async (draft: boolean) => {
      if (!name || !SCHEMA_NAME_PATTERN.test(name)) {
        setNameError(
          "Name must be alphanumeric, hyphens, or underscores (max 128 characters)"
        );
        return;
      }

      const schema = schemaRef.current;
      if (!schema.properties || Object.keys(schema.properties).length === 0) {
        setAlert({
          type: "error",
          content: "Cannot save an empty schema. Add at least one field.",
        });
        return;
      }

      setLoading(true);
      setAlert(null);

      try {
        const isDuplicate = await checkForDuplicates(name);
        if (isDuplicate) {
          setAlert({
            type: "error",
            content: `A schema with the name "${name}" already exists in Draft or Pending Approval.`,
          });
          setLoading(false);
          return;
        }

        const result = await saveSchema(STUDIO_API_CONFIG.schemaApi.saveUrl, {
          name,
          schema,
          draft,
        });

        if (result.success) {
          setAlert({
            type: "success",
            content: result.message || "Schema saved successfully.",
          });
          dismissTimerRef.current = setTimeout(() => {
            setName("");
            setNameError(undefined);
            setAlert(null);
            onDismiss();
          }, 1500);
        } else {
          setAlert({
            type: "error",
            content: result.message || "Failed to save schema.",
          });
        }
      } catch {
        setAlert({
          type: "error",
          content: "An unexpected error occurred while saving.",
        });
      } finally {
        setLoading(false);
      }
    },
    [name, checkForDuplicates, schemaRef, onDismiss]
  );

  const handleDismiss = useCallback(() => {
    if (!loading) {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
      setName("");
      setNameError(undefined);
      setAlert(null);
      onDismiss();
    }
  }, [loading, onDismiss]);

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Save Schema"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={loading || !name || !!nameError}
              loading={loading}
            >
              Save Draft
            </Button>
            <Button
              variant="primary"
              onClick={() => handleSave(false)}
              disabled={loading || !name || !!nameError}
              loading={loading}
            >
              Submit for Approval
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {alert && (
          <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
            {alert.content}
          </Alert>
        )}
        <FormField
          label="Schema name"
          errorText={nameError}
          description="Alphanumeric characters, hyphens, and underscores only (max 128 characters)"
        >
          <Input
            value={name}
            onChange={({ detail }) => handleNameChange(detail.value)}
            placeholder="my-schema-name"
            disabled={loading}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default SaveSchemaModal;
